import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ChevronDownIcon } from 'lucide-react';
type Lang = 'en' | 'es';
type FAQItem = {
  id: string;
  q: Record<Lang, string>;
  a: Record<Lang, string>;
};
const faqs: FAQItem[] = [{
  id: 'custom-designs',
  q: {
    en: 'Do you create fully custom designs?',
    es: '¿Haces diseños 100% personalizados?'
  },
  a: {
    en: 'Yes. Every piece is designed from scratch based on your idea, references, placement, and the style we define together.',
    es: 'Sí. Cada pieza se diseña desde cero según tu idea, referencias, ubicación en el cuerpo y el estilo que definamos juntos.'
  }
}, {
  id: 'booking',
  q: {
    en: 'How do I book an appointment?',
    es: '¿Cómo reservo una cita?'
  },
  a: {
    en: "Message me on WhatsApp or Instagram DM. We'll discuss your idea, placement, size, and I'll guide you through the next steps.",
    es: 'Escríbeme por WhatsApp o por DM en Instagram. Hablamos de tu idea, ubicación, tamaño y te guío con los siguientes pasos.'
  }
}, {
  id: 'location',
  q: {
    en: 'Where are you located?',
    es: '¿Dónde estás ubicado?'
  },
  a: {
    en: "I'm based in Miami, FL. After you contact me, I'll share the exact location and availability.",
    es: 'Estoy en Miami, FL. Después de contactarme te comparto la ubicación exacta y disponibilidad.'
  }
}, {
  id: 'pricing',
  q: {
    en: 'How much does a realism tattoo cost?',
    es: '¿Cuánto cuesta un tatuaje de realismo?'
  },
  a: {
    en: 'Pricing depends on size, complexity, placement, and session time. Send your idea and references for a personalized quote.',
    es: 'El precio depende del tamaño, complejidad, zona del cuerpo y tiempo de sesión. Envíame tu idea y referencias para darte una cotización personalizada.'
  }
}, {
  id: 'session-length',
  q: {
    en: 'How long does a session take?',
    es: '¿Cuánto dura una sesión?'
  },
  a: {
    en: "It depends on the piece. Some tattoos are completed in one session, while larger projects may require multiple sessions. I'll recommend the best approach for quality.",
    es: 'Depende de la pieza. Algunas se completan en una sesión y proyectos grandes pueden requerir varias. Te recomiendo el mejor plan para mantener la máxima calidad.'
  }
}, {
  id: 'pain',
  q: {
    en: 'Does it hurt?',
    es: '¿Duele?'
  },
  a: {
    en: 'Pain varies by placement and personal tolerance. I work with calm pacing and breaks when needed to make the experience manageable.',
    es: 'Depende de la zona y de cada persona. Trabajo con un ritmo calmado y hacemos pausas cuando sea necesario para que sea una experiencia manejable.'
  }
}, {
  id: 'before-appointment',
  q: {
    en: 'What should I do before my appointment?',
    es: '¿Qué debo hacer antes de mi cita?'
  },
  a: {
    en: 'Sleep well, eat beforehand, stay hydrated, avoid alcohol 24 hours prior, and follow any preparation notes I send you.',
    es: 'Duerme bien, come antes, hidrátate, evita alcohol 24 horas antes y sigue las indicaciones que te enviaré.'
  }
}, {
  id: 'aftercare',
  q: {
    en: 'How do I take care of my tattoo after?',
    es: '¿Cómo cuido el tatuaje después?'
  },
  a: {
    en: "I'll provide aftercare instructions specific to your skin and the piece. Proper aftercare is essential for healing and long-term results.",
    es: 'Te daré instrucciones de cuidado según tu piel y tu pieza. El cuidado correcto es clave para una buena cicatrización y un resultado duradero.'
  }
}, {
  id: 'deposit',
  q: {
    en: 'Do you require a deposit?',
    es: '¿Pides depósito para reservar?'
  },
  a: {
    en: 'Yes. A $100 deposit is required to secure your appointment. This deposit goes toward the final price of your tattoo.',
    es: 'Sí. Para asegurar tu cita se requiere un depósito de $100. Ese depósito se descuenta del precio final del tatuaje.'
  }
}, {
  id: 'reschedule-cancel',
  q: {
    en: 'What is your rescheduling or cancellation policy?',
    es: '¿Cuál es tu política de cambios o cancelaciones?'
  },
  a: {
    en: 'Please provide at least 24 hours notice if you need to reschedule. Deposits are non-refundable, but your $100 deposit can be transferred to a new date one time if you reschedule with 24+ hours notice.',
    es: 'Por favor avísame con al menos 24 horas de anticipación si necesitas reprogramar. El depósito no es reembolsable, pero tu depósito de $100 puede transferirse a una nueva fecha una sola vez si reprogramas con 24+ horas de aviso.'
  }
}];
const sectionCopy = {
  label: {
    en: 'FAQ',
    es: 'PREGUNTAS FRECUENTES'
  },
  title: {
    en: 'Answers Before You Book',
    es: 'Respuestas Antes de Reservar'
  },
  subtitle: {
    en: 'Clear details. Simple process. No surprises.',
    es: 'Detalles claros. Proceso simple. Sin sorpresas.'
  }
} as const;
export function FAQSection() {
  const {
    language
  } = useLanguage();
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => {
    setOpenId(prev => prev === id ? null : id);
  };
  return <section className="py-24 md:py-32 px-6 border-b border-brand-gold/30" data-id="element-36">
      <div className="max-w-3xl mx-auto" data-id="element-37">
        <motion.div className="mb-16 text-center" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} data-id="element-38">
          <span className="text-brand-gold font-sans text-sm tracking-[0.3em] uppercase block mb-4" data-id="element-39">
            {sectionCopy.label[language]}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-gold mb-4" data-id="element-40">
            {sectionCopy.title[language]}
          </h2>
          <p className="text-brand-white/60 font-sans font-light text-lg" data-id="element-41">
            {sectionCopy.subtitle[language]}
          </p>
        </motion.div>

        <div className="space-y-3" data-id="element-42">
          {faqs.map((faq, index) => {
          const isOpen = openId === faq.id;
          return <motion.div key={faq.id} initial={{
            opacity: 0,
            y: 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-30px'
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} data-id="element-43">
                <button onClick={() => toggle(faq.id)} className="w-full flex items-center justify-between gap-4 py-5 px-6 bg-brand-charcoal border border-brand-gold/20 text-left transition-all duration-300 hover:border-brand-gold/50 group" aria-expanded={isOpen} data-id="element-44">
                  <span className="font-serif text-lg text-brand-white group-hover:text-brand-gold transition-colors" data-id="element-45">
                    {faq.q[language]}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className="flex-shrink-0" data-id="element-46">
                    <ChevronDownIcon className="w-5 h-5 text-brand-gold" data-id="element-47" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-48">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-49">
                      <div className="px-6 py-5 bg-brand-black/50 border-x border-b border-brand-gold/20" data-id="element-50">
                        <p className="text-brand-white/80 font-sans font-light leading-relaxed" data-id="element-51">
                          {faq.a[language]}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}